import React from "react";
import { Icon } from "@nubeteck/icons";
import { useGlobalQuery } from "@hooks";
import { Reducers, Statuses } from "@core";
import { Button } from "primereact/button";
import { FiltersDrawer } from "@components";
import { ConfirmDialog } from "@components";
import { FilterMatchMode } from "primereact/api";
import { ArrayUtils, EventUtils } from "@nubeteck/utils";
import { confirmDialog } from "primereact/confirmdialog";
import {
  CardTable,
  StatusTag,
  PaginatorState,
  IDataTableColumnsProps,
} from "@nubeteck/prime";

import { IFinancialStatement } from "../../interfaces";
import { FinancialStatementDivisionDrawer } from "../../components";
import { useAccountingQuery, useAccountingMutation } from "../../hooks";

const AREA_FILTER_DEFAULT = "fcEstadoFinancierosBuscar";

const FinancialStatementDivisionPage = () => {
  const { data: filters } = useGlobalQuery(
    "Filters",
    "getAllByArea",
    AREA_FILTER_DEFAULT,
  );
  const changeState = useAccountingMutation(
    "FinancialStatement",
    "changeState",
  );

  const [filterSelected, setFilterSelected] = React.useState<
    number | undefined
  >();
  const [filterSettingsVisible, setFilterSettingsVisible] =
    React.useState(false);
  const [state, dispatch] = React.useReducer(Reducers.DialogReducer, {
    id: 0,
    open: false,
  });
  const [paginator, setPaginator] = React.useState<PaginatorState>({
    page: 0,
    first: 0,
    rows: 10,
  });

  const financialStatements = useAccountingQuery(
    "FinancialStatement",
    "getAllByFilter",
    {
      page: paginator.page,
      pageSize: paginator.rows,
      filterId: filterSelected ?? 0,
    },
    { enabled: !!filterSelected },
  );

  React.useEffect(() => {
    const defaultFilterId = filters?.find(
      (filter) => filter.EsPorDefecto,
    )?.FiltroId;
    setFilterSelected(defaultFilterId);
  }, [filters]);

  const handleNullify = React.useCallback(
    (id: number) => {
      confirmDialog({
        acceptLabel: "Anular",
        header: "Anular division de estado financiero",
        message:
          "¿Está seguro de que desea anular esta division de estado financiero?",
        accept: async () => {
          await changeState.mutateAsync({
            Id: id,
            EstadoId: Statuses.NULLIFIED_CODE,
          });
        },
      });
    },
    [changeState],
  );

  const handleRestore = React.useCallback(
    (id: number) => {
      confirmDialog({
        acceptLabel: "Activar",
        acceptClassName: "p-button-sm",
        header: "Activar division de estado financiero",
        message:
          "¿Está seguro de que desea activar esta division de estado financiero?",
        accept: async () => {
          await changeState.mutateAsync({
            Id: id,
            EstadoId: Statuses.ACTIVE_CODE,
          });
        },
      });
    },
    [changeState],
  );

  const columns: IDataTableColumnsProps<IFinancialStatement>[] = [
    {
      header: "No.",
      sortable: true,
      field: "EstadoFinancieroId",
    },
    {
      filter: true,
      sortable: true,
      field: "Nombre",
      dataType: "text",
      header: "Nombre",
      filterMatchMode: FilterMatchMode.STARTS_WITH,
    },
    {
      field: "TipoEstadoFinancieroNombre",
      header: "Tipo de estado financiero",
    },
    {
      header: "Estado",
      field: "EstadoNombre",
      body: (data) => <StatusTag status={data.EstadoNombre ?? "Activo"} />,
    },
  ];

  const filtersOptions = React.useMemo(() => {
    if (!filters?.length) return [];
    return ArrayUtils.selectLabelValue(filters ?? [], "Nombre", "FiltroId");
  }, [filters]);

  return (
    <>
      <CardTable<IFinancialStatement>
        type="data"
        columns={columns}
        rows={paginator.rows}
        first={paginator.first}
        hideSelectFilter={false}
        dataKey="EstadoFinancieroId"
        valueFilter={filterSelected}
        filterOptions={filtersOptions}
        loading={financialStatements.isPending}
        title="Divisiones de estados financieros"
        value={financialStatements.data?.Data ?? []}
        onRefresh={() => financialStatements.refetch()}
        onPaginationChange={(event) => setPaginator(event)}
        totalRecords={financialStatements.data?.TotalRecords}
        emptyMessage="No hay divisiones de estados financieros"
        onSelectFilterOption={(value) => setFilterSelected(value)}
        onClickFilterSettings={EventUtils.callEvent(
          setFilterSettingsVisible,
          true,
        )}
        headActions={[
          <Button
            size="small"
            label="Nuevo estado financiero"
            key="ButtonAddFinancialStateDivision"
            icon={<Icon size={20} name="plus" className="mr-1" />}
            onClick={EventUtils.callEvent(dispatch, {
              payload: 0,
              type: "OPEN_DIALOG",
            })}
          />,
        ]}
        tableActions={(data) => [
          {
            icon: "pencil",
            label: "Editar",
            onClick: EventUtils.callEvent(dispatch, {
              type: "OPEN_DIALOG",
              payload: data.EstadoFinancieroId,
            }),
          },
          {
            icon: "ban",
            label: "Anular",
            iconClassName: "text-red-500",
            disabled: data.EstadoId === Statuses.NULLIFIED_CODE,
            onClick: EventUtils.callEvent(
              handleNullify,
              data.EstadoFinancieroId,
            ),
          },
          {
            icon: "plug",
            label: "Activar",
            iconClassName: "text-primary-500",
            disabled: data.EstadoId === Statuses.ACTIVE_CODE,
            onClick: EventUtils.callEvent(
              handleRestore,
              data.EstadoFinancieroId,
            ),
          },
        ]}
      />
      <FinancialStatementDivisionDrawer
        id={state.id}
        open={state.open}
        onHide={EventUtils.callEvent(dispatch, { type: "CLOSE_DIALOG" })}
      />
      <FiltersDrawer
        area={AREA_FILTER_DEFAULT}
        visible={filterSettingsVisible}
        onHide={EventUtils.callEvent(setFilterSettingsVisible, false)}
      />
      <ConfirmDialog />
    </>
  );
};

export default FinancialStatementDivisionPage;
